import {DataGridEnumColumnItem} from "./data-grid-enum-column-item.model";

/**
 * Klasyczny model definicji kolumny dla komponentu DataGrid.
 * Opisuje powiązanie kolumny z danymi, sposób jej renderowania, zachowanie w wyszukiwarce
 * oraz widoczność na urządzeniach mobilnych.
 */
export class DataGridColumnDef {

  /** Klucz właściwości w obiekcie wiersza (np. 'name', 'taxId'), z którego kolumna czerpie dane. */
  public columnKey: string;

  /** Jawna etykieta tekstowa wyświetlana w nagłówku kolumny (Header). */
  public columnLabel: string;

  /** Definiuje typ kontrolki wyszukiwania renderowanej pod nagłówkiem kolumny.
   * Obsługuje wartości tekstowe, logiczne, interwały liczbowe oraz listy wyboru (enum).
   */
  public searchFieldType: 'none' | 'string' | 'boolean' | 'object' | 'numberInterval' | 'enum';

  /** Opcjonalna funkcja formatująca (Callback), przekształcająca surową wartość z bazy
   * na czytelny dla człowieka ciąg znaków (np. formatowanie daty, waluty).
   */
  public toStringCallback: any;

  /** Funkcja pomocnicza używana zazwyczaj do rzutowania/formatowania zagnieżdżonych obiektów. */
  public toStringItemCallback: any;

  /** Tablica dostępnych opcji wyboru (dla filtru typu 'enum'). */
  public enumItems: string[];

  /** Ścieżka dostępu do zagnieżdżonych danych (np. 'company.address.city'). */
  public columnKeyFullPath: string;

  /** Flaga biznesowa określająca, czy kolumna ma być renderowana w widoku klienckim. */
  public forClient: boolean;

  /** Flaga Responsive Web Design (RWD).
   * Określa, czy kolumna ma pozostać widoczna na wąskich ekranach urządzeń mobilnych.
   */
  public showOnMobile: boolean;

  constructor(
    forClient: boolean,
    showOnMobile: boolean,
    columnKey: string,
    columnLabel: string,
    searchFieldType: 'none' | 'string' | 'boolean' | 'numberInterval' | 'enum'= 'string',
    toStringCallback: any = undefined,
    enumItems: string[] = undefined
  ) {
    this.forClient = forClient;
    this.showOnMobile = showOnMobile;
    this.columnKey = columnKey;
    this.columnLabel = columnLabel;
    this.searchFieldType = searchFieldType;
    this.toStringCallback = toStringCallback;
    this.enumItems = enumItems;
  }
}

/**
 * Zoptymalizowany model definicji kolumny, pozbawiony sztywnej etykiety 'columnLabel'.
 */
export class SimpleDataGridColumnDef {

  public columnKey: string;
  public searchFieldType: 'none' | 'string' | 'boolean' | 'object' | 'numberInterval' | 'enum' | 'objectNone';
  public toStringCallback: any;
  public toStringItemCallback: any

  /** Rozszerzone wsparcie dla słowników - akceptuje zarówno proste tablice, jak i obiekty DataGridEnumColumnItem. */
  public enumItems: string[] | DataGridEnumColumnItem[];

  public columnKeyFullPath: string;
  public forClient: boolean;
  public showOnMobile: boolean;

  /** * Opcja wyłączająca możliwość sortowania i wyszukiwania po tej konkretnej kolumnie
   * (przydatne np. dla kolumn z wygenerowanymi w locie przyciskami akcji).
   */
  public searchAndSortDisabled: boolean = false;

  constructor(
    forClient: boolean,
    showOnMobile: boolean,
    columnKey: string,
    searchFieldType: 'none' | 'string' | 'boolean' | 'object' | 'objectNone' | 'numberInterval' | 'enum' = 'string',
    toStringCallback: any = undefined,
    enumItems: string[] | DataGridEnumColumnItem[] = undefined,
    searchSortDisabled: boolean = false
  ) {
    this.forClient = forClient;
    this.showOnMobile = showOnMobile;
    this.columnKey = columnKey;
    this.searchFieldType = searchFieldType;
    this.toStringCallback = toStringCallback;
    this.enumItems = enumItems;
    this.searchAndSortDisabled = searchSortDisabled;
  }
}

/**
 * Model kolumny grupującej.
 * Umożliwia scalenie danych z kilku różnych właściwości (columnKeys) wewnątrz jednej, fizycznej komórki tabeli
 * (np. połączenie pól 'firstName' i 'lastName' w jedną kolumnę wyświetlającą pełne dane).
 */
export class SimpleDataGridColumnDefGroup extends SimpleDataGridColumnDef {

  public toStringItemCallback: any;

  /** Tablica kluczy danych wchodzących w skład zgrupowanej komórki. */
  public columnKeys: string[];
  /** Flaga decydująca, czy wyszukiwanie kolumny ma być przeprowadzone w trybie adaptacyjnym.
   * Jeśli użytkownik wyszuka pole frazą składającą się z columnKeys.length słów,
   * nastąpi wyszukiwanie w trybie AND (z rozdzielonymi kolumnami bazowymi).
   * W przeciwnym wypadku nastąpi standardowe wyszukiwanie OR ze znacznikiem CMPLX.
   */
  public adaptiveSearch: boolean;

  constructor(
    forClient: boolean,
    showOnMobile: boolean,
    columnKey: string,
    searchFieldType: "none" | "string" | "boolean" | "object" | "objectNone" | "numberInterval" | "enum",
    toStringCallback: any,
    enumItems: string[] | DataGridEnumColumnItem[],
    toStringItemCallback: any,
    columnKeys: string[],
    adaptiveSearch: boolean = false
  ) {
    super(forClient, showOnMobile, columnKey, searchFieldType, toStringCallback, enumItems);
    this.toStringItemCallback = toStringItemCallback;
    this.columnKeys = columnKeys;
    this.adaptiveSearch = adaptiveSearch;
  }
}
